import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ChevronRight, Loader2, Search, X } from 'lucide-react';
import { pinyin } from 'pinyin-pro';
import * as OpenCC from 'opencc-js';
import { KangxiCharLookupResult, lookupKangxiCharApi } from '../lib/api';
import XuanYuanTopBar from './XuanYuanTopBar';

const toTraditional = OpenCC.Converter({ from: 'cn', to: 'hk' });
const KAITI_FONT = "font-['Kaiti','STKaiti','KaiTi_GB2312','楷体','serif']";

function getInitialCharFromUrl(): string | null {
  try {
    const params = new URLSearchParams(window.location.search);
    const chars = Array.from(params.get('char')?.trim() || '');
    const char = chars.length > 0 ? chars[0] : '';
    return char || null;
  } catch {
    return null;
  }
}

function takeLastCodePoint(raw: string): string {
  const chars = Array.from(String(raw || '').trim());
  if (chars.length === 0) return '';
  return chars[chars.length - 1];
}

function isHanChar(char: string): boolean {
  const value = String(char || '');
  if (Array.from(value).length !== 1) return false;
  const cp = value.codePointAt(0);
  if (!cp) return false;
  if (cp >= 0x3400 && cp <= 0x4dbf) return true;
  if (cp >= 0x4e00 && cp <= 0x9fff) return true;
  if (cp >= 0xf900 && cp <= 0xfaff) return true;
  if (cp >= 0x20000 && cp <= 0x323af) return true;
  return false;
}

function formatPinyin(result: KangxiCharLookupResult | null, charDisplay: string): string {
  if (result?.pinyin?.trim()) {
    return result.pinyin.trim();
  }
  try {
    const py = pinyin(charDisplay, { toneType: 'symbol' }) || '';
    return py.trim() || '-';
  } catch {
    return '-';
  }
}

const PINYIN_TOKEN_RE = /^[A-Za-zāáǎàōóǒòēéěèīíǐìūúǔùǖǘǚǜüɡ·\-]+$/;

function normalizePinyinToken(raw: string): string {
  return String(raw || '')
    .trim()
    .replace(/ɡ/g, 'g')
    .replace(/[·•]+/g, '·')
    .replace(/\s+/g, ' ');
}

function extractPinyinTokensFromText(raw: string): string[] {
  const matches = String(raw || '').match(/[A-Za-zāáǎàōóǒòēéěèīíǐìūúǔùǖǘǚǜüɡ·\-]+/g);
  if (!matches) return [];
  return matches
    .map((token) => normalizePinyinToken(token))
    .filter((token) => token.length >= 1 && PINYIN_TOKEN_RE.test(token));
}

function extractLeadingPinyinToken(raw: string): string {
  const m = String(raw || '').trim().match(/^([A-Za-zāáǎàōóǒòēéěèīíǐìūúǔùǖǘǚǜüɡ·\-]+)/);
  if (!m) return '';
  const token = normalizePinyinToken(m[1]);
  return PINYIN_TOKEN_RE.test(token) ? token : '';
}

function collectXinhuaPinyins(
  charDisplay: string,
  xinhuaLines: string[],
  fallbackPinyinText: string,
): string[] {
  const results: string[] = [];
  const seen = new Set<string>();
  const push = (raw: string) => {
    const token = normalizePinyinToken(raw);
    if (!token) return;
    const key = token.toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    results.push(token);
  };

  extractPinyinTokensFromText(fallbackPinyinText).forEach(push);

  if (!charDisplay || xinhuaLines.length === 0) {
    return results;
  }

  const charHeaderRe = new RegExp(`^${escapeRegExp(charDisplay)}(?:[⁰¹²³⁴⁵⁶⁷⁸⁹]+)?$`);
  const inlineRe = new RegExp(`^${escapeRegExp(charDisplay)}\\s+(.+)$`);

  for (let i = 0; i < xinhuaLines.length; i += 1) {
    const line = String(xinhuaLines[i] || '').trim();
    if (!line) continue;

    const inline = line.match(inlineRe);
    if (inline) {
      const token = extractLeadingPinyinToken(inline[1]);
      if (token) push(token);
    }

    if (charHeaderRe.test(line) && i + 1 < xinhuaLines.length) {
      const nextLine = String(xinhuaLines[i + 1] || '').trim();
      const token = extractLeadingPinyinToken(nextLine);
      if (token) push(token);
    }
  }

  return results;
}

function safeTraditional(result: KangxiCharLookupResult | null, charDisplay: string): string {
  try {
    const converted = toTraditional(charDisplay);
    if (converted && Array.from(converted).length === 1 && converted !== charDisplay) {
      return converted;
    }
  } catch {
    // Ignore conversion errors and continue fallback.
  }
  if (result?.traditional && Array.from(result.traditional).length === 1) {
    return result.traditional;
  }
  try {
    const converted = toTraditional(charDisplay);
    return converted && Array.from(converted).length === 1 ? converted : charDisplay;
  } catch {
    return charDisplay;
  }
}

function pickStroke(value: unknown): number {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? Math.trunc(n) : 0;
}

function escapeRegExp(text: string): string {
  return String(text || '').replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

const CIRCLED_TOP_NUM_RE = /[⒈⒉⒊⒋⒌⒍⒎⒏⒐⒑⒒⒓⒔⒕⒖⒗⒘⒙⒚⒛]/;
const CIRCLED_SUB_NUM_RE = /[①②③④⑤⑥⑦⑧⑨⑩⑪⑫⑬⑭⑮⑯⑰⑱⑲⑳]/;
const SUPERSCRIPT_DIGIT_MAP: Record<string, string> = {
  '0': '⁰', '1': '¹', '2': '²', '3': '³', '4': '⁴',
  '5': '⁵', '6': '⁶', '7': '⁷', '8': '⁸', '9': '⁹',
};

function normalizeChinesePunctuation(raw: string): string {
  return String(raw || '')
    .replace(/\r/g, '')
    .replace(/,/g, '，')
    .replace(/;/g, '；')
    .replace(/:/g, '：')
    .replace(/\?/g, '？')
    .replace(/!/g, '！')
    .replace(/\(/g, '（')
    .replace(/\)/g, '）')
    .replace(/\[/g, '【')
    .replace(/]/g, '】')
    .replace(/\.{3}/g, '……')
    .replace(/。{2,}/g, '。');
}

function normalizeDictionaryText(raw: string): string {
  let text = normalizeChinesePunctuation(raw);
  text = text
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n[ \t]+/g, '\n')
    .replace(/[ \t]{2,}/g, ' ')
    .replace(/\n{3,}/g, '\n\n');
  text = text
    .replace(/([⒈⒉⒊⒋⒌⒍⒎⒏⒐⒑⒒⒓⒔⒕⒖⒗⒘⒙⒚⒛])\s*\n+\s*([①②③④⑤⑥⑦⑧⑨⑩⑪⑫⑬⑭⑮⑯⑰⑱⑲⑳])/g, '$1$2')
    .replace(/([⒈⒉⒊⒋⒌⒍⒎⒏⒐⒑⒒⒓⒔⒕⒖⒗⒘⒙⒚⒛])\s*\n+\s*/g, '$1')
    .replace(/([①②③④⑤⑥⑦⑧⑨⑩⑪⑫⑬⑭⑮⑯⑰⑱⑲⑳])\s*\n+\s*/g, '$1')
    .replace(/([（(]\d+[）)])\s*\n+\s*/g, '$1 ')
    .replace(/(\d+[、.．])\s*\n+\s*/g, '$1 ')
    .replace(/\n{2,}(?=[（(]\d+[）)]\s*)/g, '\n');
  return text.trim();
}

function toSuperscriptDigits(rawDigits: string): string {
  return String(rawDigits || '')
    .split('')
    .map((d) => SUPERSCRIPT_DIGIT_MAP[d] || d)
    .join('');
}

function isMetaEtymologyLine(text: string): boolean {
  const line = String(text || '').trim();
  if (!line) return false;
  return /^（?(会意|形声|象形|指事|会義|会义|本义|本義)/.test(line);
}

function isPinyinLikeLine(text: string): boolean {
  const line = String(text || '').trim();
  if (!line) return false;
  return /^[A-Za-zāáǎàōóǒòēéěèīíǐìūúǔùǖǘǚǜü·\-\s]+$/.test(line);
}

function shouldStripNumberingFromLine(text: string): boolean {
  const line = String(text || '').trim();
  if (!line) return true;
  if (isMetaEtymologyLine(line)) return true;
  if (/^[\u4e00-\u9fff]{1,4}$/.test(line)) return true;
  if (isPinyinLikeLine(line)) return true;
  return false;
}

function isCharHeaderLine(line: string): boolean {
  const text = String(line || '').trim();
  if (!text) return false;
  if (/^[\u4e00-\u9fff][⁰¹²³⁴⁵⁶⁷⁸⁹]+$/.test(text)) return true;
  if (/^[\u4e00-\u9fff]{1,2}\s+[A-Za-zāáǎàōóǒòēéěèīíǐìūúǔùǖǘǚǜü·\-\s]+$/.test(text)) return true;
  if (/^[\u4e00-\u9fff]$/.test(text)) return true;
  return false;
}

function formatXinhuaTextForDisplay(raw: string): string {
  const baseLines = normalizeDictionaryText(raw)
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean);
  if (baseLines.length === 0) return '';

  const mergedLines: string[] = [];
  for (let i = 0; i < baseLines.length; i += 1) {
    const line = baseLines[i];
    const next = baseLines[i + 1] || '';

    const headerMatch = line.match(/^([\u4e00-\u9fff])([0-9]+)$/);
    if (headerMatch) {
      mergedLines.push(`${headerMatch[1]}${toSuperscriptDigits(headerMatch[2])}`);
      continue;
    }

    const tradOnly = /^[\u4e00-\u9fff]{1,2}$/.test(line);
    const nextIsPinyin = /^[A-Za-zāáǎàōóǒòēéěèīíǐìūúǔùǖǘǚǜü·\-\s]+$/.test(next);
    if (tradOnly && nextIsPinyin) {
      mergedLines.push(`${line} ${next}`.replace(/[ ]{2,}/g, ' ').trim());
      i += 1;
      continue;
    }

    mergedLines.push(line);
  }

  const renumbered: string[] = [];
  let sectionCounter = 0;
  for (const line of mergedLines) {
    let nextLine = line;
    if (isCharHeaderLine(nextLine)) {
      sectionCounter = 0;
      renumbered.push(nextLine);
      continue;
    }
    const numbered = nextLine.match(/^（(\d+)）\s*(.*)$/);
    if (numbered) {
      const rest = String(numbered[2] || '').trim();
      if (shouldStripNumberingFromLine(rest)) {
        // 造字法/本义说明不作为释义条目编号。
        nextLine = rest;
      } else {
        sectionCounter += 1;
        nextLine = `（${sectionCounter}） ${rest}`;
      }
    }
    renumbered.push(nextLine);
  }

  const finalMerged: string[] = [];
  for (let i = 0; i < renumbered.length; i += 1) {
    const cur = String(renumbered[i] || '').trim();
    const next = String(renumbered[i + 1] || '').trim();
    if (/^[\u4e00-\u9fff]{1,2}$/.test(cur) && isPinyinLikeLine(next)) {
      finalMerged.push(`${cur} ${next}`.replace(/[ ]{2,}/g, ' ').trim());
      i += 1;
      continue;
    }
    finalMerged.push(cur);
  }

  return finalMerged
    .join('\n')
    .replace(/\n(?=[①②③④⑤⑥⑦⑧⑨⑩⑪⑫⑬⑭⑮⑯⑰⑱⑲⑳])/g, ' ')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

function sanitizeModernBasicText(charDisplay: string, modernText: string): string {
  const char = String(charDisplay || '').trim();
  const normalized = normalizeDictionaryText(modernText);
  if (!normalized) return '';

  const lines = normalized
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean);
  if (lines.length === 0) return '';

  const topNumIndex = lines.findIndex((line) => CIRCLED_TOP_NUM_RE.test(line));
  let startIndex = 0;
  if (topNumIndex > 0) {
    startIndex = topNumIndex;
    const prevLine = lines[topNumIndex - 1] || '';
    const charPinyinPattern = new RegExp(`^${escapeRegExp(char)}[A-Za-zāáǎàōóǒòēéěèīíǐìūúǔùǖǘǚǜü\\s·-]+$`);
    if (charPinyinPattern.test(prevLine)) {
      startIndex = topNumIndex - 1;
    }
  }

  const kept = lines.slice(startIndex);
  return normalizeDictionaryText(kept.join('\n'));
}

function sanitizeXinhuaBasicText(charDisplay: string, modernMore: string): string {
  const char = String(charDisplay || '').trim();
  const rawMore = String(modernMore || '').trim();
  if (!char || !rawMore) return '';

  const normalizedMore = normalizeDictionaryText(rawMore);
  const lines = normalizedMore
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean);
  if (lines.length === 0) return '';

  const metaLinePattern = /部首|部首笔画|总笔画|郑码|gbk|笔顺编号|unicode|u[0-9a-f]{4}|搜索与/i;
  const xinhuaLines = lines.filter((line) => !metaLinePattern.test(line));
  if (xinhuaLines.length === 0) return '';

  const sectionHeaderPattern = new RegExp(`^${escapeRegExp(char)}\\d+$`);
  const sectionIndexes: number[] = [];
  for (let i = 0; i < xinhuaLines.length; i += 1) {
    if (sectionHeaderPattern.test(xinhuaLines[i])) {
      sectionIndexes.push(i);
    }
  }

  const hasNumberedSense = (arr: string[]): boolean =>
    arr.some((line) => /^[（(]\d+[）)]/.test(line) || /^\d+[、.．]/.test(line));

  const cutAtSeeAlso = (arr: string[]): string[] => {
    const idx = arr.findIndex((line) => /^另见/u.test(line));
    if (idx >= 0) return arr.slice(0, idx + 1);
    return arr;
  };

  // 多音字优先：按“字1/字2/...”分段，分别保留每个读音的核心释义。
  if (sectionIndexes.length > 0) {
    const grouped: string[] = [];
    for (let i = 0; i < sectionIndexes.length; i += 1) {
      const start = sectionIndexes[i];
      const end = i + 1 < sectionIndexes.length ? sectionIndexes[i + 1] : xinhuaLines.length;
      const rawSection = xinhuaLines.slice(start, end).map((line) => line.trim()).filter(Boolean);
      if (rawSection.length === 0) continue;
      // 过滤末尾“注音速览”短节，只保留完整释义段。
      if (rawSection.some((line) => /[ㄅ-ㄩ]/.test(line))) continue;
      if (!hasNumberedSense(rawSection)) continue;

      const section = cutAtSeeAlso(rawSection);
      if (section.length === 0) continue;
      if (grouped.length > 0) grouped.push('');
      grouped.push(...section);
    }

    if (grouped.length > 0) {
      return formatXinhuaTextForDisplay(grouped.join('\n'));
    }
  }

  const charStartPattern = new RegExp(`^${escapeRegExp(char)}(?:\\d+|[（(]|\\s|$)`);
  const startIndex = xinhuaLines.findIndex((line) => charStartPattern.test(line));
  const fromCharEntry = startIndex >= 0 ? xinhuaLines.slice(startIndex) : xinhuaLines;

  const wordEntryPattern = new RegExp(`^${escapeRegExp(char)}[\\u4e00-\\u9fff]{1,}$`);
  let sawNumberedSense = false;
  const kept: string[] = [];
  for (const line of fromCharEntry) {
    if (/^另见/u.test(line)) {
      kept.push(line);
      break;
    }
    if (/^[（(]\d+[）)]/.test(line) || /^\d+[、.．]/.test(line)) {
      sawNumberedSense = true;
    }
    if (sawNumberedSense && wordEntryPattern.test(line) && line !== char) {
      break;
    }
    kept.push(line);
  }

  if (kept.length === 0) return '';

  const deduped: string[] = [];
  const seen = new Set<string>();
  for (const line of kept) {
    const key = line.trim();
    if (!key) continue;
    if (seen.has(key)) continue;
    seen.add(key);
    deduped.push(key);
  }

  return formatXinhuaTextForDisplay(deduped.join('\n'));
}

function isXinhuaQuoteLine(line: string): boolean {
  const text = String(line || '').trim();
  if (!text) return false;
  if (/^>\s?/.test(text)) return true;
  return /(?:--|——|—)\s*[^\n]*《[^》]+》/.test(text);
}

function normalizeXinhuaQuoteLine(line: string): string {
  return String(line || '')
    .trim()
    .replace(/^>\s?/, '')
    .replace(/\[!.*?\]\s*/g, '')
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
    .trim();
}

function buildXinhuaRenderBlocks(lines: string[]): Array<
  { kind: 'text'; text: string; isBigHeader: boolean } |
  { kind: 'quote'; lines: string[] }
> {
  const blocks: Array<
    { kind: 'text'; text: string; isBigHeader: boolean } |
    { kind: 'quote'; lines: string[] }
  > = [];
  let quoteBuffer: string[] = [];

  const flushQuoteBuffer = () => {
    if (quoteBuffer.length === 0) return;
    blocks.push({ kind: 'quote', lines: [...quoteBuffer] });
    quoteBuffer = [];
  };

  for (const raw of lines) {
    const line = String(raw || '').trim();
    if (!line) continue;
    if (isXinhuaQuoteLine(line)) {
      const quoteLine = normalizeXinhuaQuoteLine(line);
      if (quoteLine) {
        quoteBuffer.push(quoteLine);
      }
      continue;
    }

    flushQuoteBuffer();
    blocks.push({
      kind: 'text',
      text: line,
      isBigHeader: /^[\u4e00-\u9fff][⁰¹²³⁴⁵⁶⁷⁸⁹]+$/.test(line),
    });
  }

  flushQuoteBuffer();
  return blocks;
}

function formatKangxiText(raw: string): string {
  let text = normalizeChinesePunctuation(raw)
    .replace(/　+/g, '')
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n[ \t]+/g, '\n')
    // 合并无意义的断行，避免“一句话一行”的排版。
    .replace(/([^\n。；！？])\n(?!\n|【|又【|考證：|考证：)/g, '$1')
    // 文言原文中“。又...”常为新义项，单独断行为宜读。
    .replace(/。又/g, '。\n又')
    .replace(/(?<!\n)(又【)/g, '\n$1')
    .replace(/(?<!\n)(考證：?|考证：?)/g, '\n$1')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
  return text;
}

interface KangxiDictionaryProps {
  onBack: () => void;
  initialChar?: string | null;
  onOpenSancaiLiuge?: (fullName: string) => void;
}

const KangxiDictionary: React.FC<KangxiDictionaryProps> = ({
  onBack,
  initialChar: initialCharProp,
  onOpenSancaiLiuge,
}) => {
  const [inputChar, setInputChar] = useState('');
  const [result, setResult] = useState<KangxiCharLookupResult | null>(null);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [isComposing, setIsComposing] = useState(false);

  const cacheRef = useRef<Map<string, KangxiCharLookupResult>>(new Map());
  const autoSearchedRef = useRef(false);
  const requestSeqRef = useRef(0);

  const doSearch = useCallback(async (rawChar: string) => {
    const char = takeLastCodePoint(rawChar);
    const seq = requestSeqRef.current + 1;
    requestSeqRef.current = seq;
    setInputChar(char);
    setSearchError(null);
    setNotFound(false);
    setResult(null);

    if (!char) return;
    if (!isHanChar(char)) {
      setSearchError('请输入一个汉字。');
      return;
    }

    if (cacheRef.current.has(char)) {
      const hit = cacheRef.current.get(char) as KangxiCharLookupResult;
      if (seq !== requestSeqRef.current) return;
      const hasInfo = Boolean(
        pickStroke(hit.stroke) > 0
        || hit.kangxiText
        || hit.modernText
        || hit.modernMore
        || hit.pinyin
      );
      setResult(hit);
      setNotFound(!hasInfo);
      if (hit.error) setSearchError(hit.error);
      return;
    }

    setSearching(true);
    try {
      const data = await lookupKangxiCharApi(char);
      if (seq !== requestSeqRef.current) return;
      cacheRef.current.set(char, data);
      const hasInfo = Boolean(
        pickStroke(data.stroke) > 0
        || data.kangxiText
        || data.modernText
        || data.modernMore
        || data.pinyin
      );
      setResult(data);
      setNotFound(!hasInfo);
      if (data.error) {
        setSearchError(data.error);
      }
    } catch (e) {
      if (seq !== requestSeqRef.current) return;
      setSearchError(e instanceof Error ? e.message : '查询失败，请稍后重试');
      setNotFound(false);
    } finally {
      if (seq === requestSeqRef.current) {
        setSearching(false);
      }
    }
  }, []);

  useEffect(() => {
    const fromUrl = getInitialCharFromUrl();
    const char = initialCharProp ?? fromUrl;
    if (char && !autoSearchedRef.current) {
      autoSearchedRef.current = true;
      void doSearch(char);
    }
  }, [initialCharProp, doSearch]);

  const handleSearch = () => {
    if (isComposing) return;
    void doSearch(inputChar);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const next = e.target.value;
    if (isComposing) {
      setInputChar(next);
      return;
    }
    setInputChar(takeLastCodePoint(next));
  };

  const handleCompositionStart = () => {
    setIsComposing(true);
  };

  const handleCompositionEnd = (e: React.CompositionEvent<HTMLInputElement>) => {
    setIsComposing(false);
    setInputChar(takeLastCodePoint(e.currentTarget.value));
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleSearch();
  };

  const charDisplay = (result?.char || inputChar || '').trim();
  const hasResult = Boolean(
    result
    && (
      pickStroke(result.stroke) > 0
      || result.kangxiText
      || result.modernText
      || result.modernMore
      || result.pinyin
    )
  );

  const fallbackPinyinText = charDisplay ? formatPinyin(result, charDisplay) : '-';
  const traditional = charDisplay ? safeTraditional(result, charDisplay) : '';
  const simplifiedStroke = pickStroke(result?.simplifiedStroke);
  const backendTraditional = String(result?.traditional || '').trim();
  const backendTraditionalStroke = pickStroke(result?.traditionalStroke);
  const strokeSource = String(result?.source || '').trim();
  const canUseBackendTraditionalStroke = Boolean(
    backendTraditional
    && traditional
    && backendTraditional === traditional
    && backendTraditionalStroke > 0
  );
  const canFallbackToKangxiStroke = pickStroke(result?.stroke) > 0
    && strokeSource !== 'modern'
    && strokeSource !== 'nameology';
  const traditionalStroke = canUseBackendTraditionalStroke
    ? backendTraditionalStroke
    : (traditional === charDisplay && canFallbackToKangxiStroke ? pickStroke(result?.stroke) : 0);
  const nameologyStroke = pickStroke(result?.nameologyStroke) || pickStroke(result?.stroke);
  const xinhuaBasicText = sanitizeXinhuaBasicText(charDisplay, result?.modernMore || '');
  const kangxiFormattedText = formatKangxiText(result?.kangxiText || '');
  const xinhuaLines = xinhuaBasicText
    ? xinhuaBasicText.split('\n').map((line) => line.trim()).filter(Boolean)
    : [];
  const xinhuaPinyins = collectXinhuaPinyins(charDisplay, xinhuaLines, fallbackPinyinText);
  const pinyinText = xinhuaPinyins.length > 0 ? xinhuaPinyins.join(' / ') : fallbackPinyinText;
  const xinhuaRenderBlocks = buildXinhuaRenderBlocks(xinhuaLines);
  const centerSearchPanel = !hasResult && !searchError && !notFound;

  return (
    <div className="min-h-screen bg-[radial-gradient(120%_95%_at_50%_-10%,#f0dfbc_0%,#e8d3ae_30%,#f4ead6_55%,#eef1f6_100%)]">
      <XuanYuanTopBar
        title="轩辕国学·康熙字典"
        onBack={onBack}
        variant="lightBlend"
        maxWidthClassName="max-w-3xl"
      />

      <main className="mx-auto max-w-3xl pb-20">
        <section
          className={`px-4 sm:px-6 ${centerSearchPanel ? 'min-h-[56vh] py-6 flex items-center' : 'pb-5 pt-7'}`}
        >
          <div className="flex w-full items-center gap-4 sm:gap-5">
            <div
              className={`shrink-0 text-xl leading-[1] text-[#6B7280] sm:text-2xl ${KAITI_FONT}`}
              style={{ writingMode: 'vertical-rl', textOrientation: 'upright', letterSpacing: '0.1em' }}
            >
              康熙字典
            </div>

            <div className="relative flex-1 rounded-3xl border border-[#dcc8a6] bg-[linear-gradient(180deg,rgba(255,250,240,0.95)_0%,rgba(255,255,255,0.9)_100%)] p-2 shadow-[0_12px_32px_rgba(121,88,39,0.12)] backdrop-blur-sm">
              <input
                type="text"
                value={inputChar}
                onChange={handleInputChange}
                onCompositionStart={handleCompositionStart}
                onCompositionEnd={handleCompositionEnd}
                onKeyDown={handleKeyDown}
                placeholder="输入单字"
                className="h-12 w-full rounded-2xl border border-[#D8D0C2] bg-white px-4 pr-20 text-xl text-[#3B3530] outline-none placeholder:text-slate-300 focus:border-[#BFA97F]"
              />

              {inputChar && (
                <button
                  onClick={() => {
                    setInputChar('');
                    setResult(null);
                    setSearchError(null);
                    setNotFound(false);
                  }}
                  className="absolute right-12 top-8 -translate-y-1/2 rounded-full bg-[#f1e8d6] p-1.5 text-[#8b6b3d] hover:bg-[#e7dcc5]"
                  aria-label="清空"
                >
                  <X className="h-5 w-5" />
                </button>
              )}

              <button
                onClick={handleSearch}
                disabled={searching || !inputChar.trim()}
                className="absolute right-2 top-8 -translate-y-1/2 rounded-2xl bg-[#8b6b3d] p-2.5 text-white hover:bg-[#76582f] disabled:cursor-not-allowed disabled:bg-slate-300"
                aria-label="查询"
              >
                {searching ? <Loader2 className="h-5 w-5 animate-spin" /> : <Search className="h-5 w-5" />}
              </button>
            </div>
          </div>
        </section>

        <section className="px-4 py-4 sm:px-6">
          {searchError && (
            <div className="mb-4 rounded-2xl border border-[#E4C9A5] bg-[#FFF6E7] px-4 py-3 text-sm text-[#8A6438]">
              {searchError}
            </div>
          )}

          {notFound && charDisplay && (
            <div className="mb-4 rounded-2xl border border-[#E6DED0] bg-white/95 px-4 py-6 text-center text-[#7C7063] shadow-[0_8px_24px_rgba(92,74,39,0.05)]">
              <p className="text-base">暂未检索到「{charDisplay}」的完整字典条目</p>
            </div>
          )}

          {hasResult && result && charDisplay && (
            <div className="space-y-6">
              <article className="rounded-3xl border border-[#E4DDD0] bg-white p-4 shadow-[0_8px_24px_rgba(92,74,39,0.06)] sm:p-5">
                <p className="mb-3 text-sm text-[#C8A655] sm:text-[13px]">
                  拼音：<span className="font-medium">[{pinyinText}]</span>
                </p>

                <div className="grid grid-cols-[96px_1fr] gap-4 sm:grid-cols-[112px_1fr] sm:gap-6">
                  <div className="relative h-24 w-24 border border-[#D9D0C1] bg-[#FFFCF4] sm:h-28 sm:w-28">
                    <div className="absolute inset-0 border border-[#E6DCC8]" />
                    <div className="absolute left-1/2 top-0 h-full w-px -translate-x-1/2 bg-[#E8DECD]" />
                    <div className="absolute top-1/2 left-0 h-px w-full -translate-y-1/2 bg-[#E8DECD]" />
                    <div className={`absolute inset-0 flex items-center justify-center text-4xl text-[#516D92] sm:text-5xl ${KAITI_FONT}`}>
                      {charDisplay}
                    </div>
                  </div>

                  <div className="space-y-2 text-sm text-[#3F3831] sm:text-[13px]">
                    <div className="grid grid-cols-2 gap-x-4 gap-y-1.5">
                      <p>部首：{result.radicals || '—'}</p>
                      <p>结构：{result.structure || '—'}</p>
                      <p>繁体：{traditional || '—'}</p>
                      <p>五行：{result.wuxing || '—'}</p>
                    </div>
                    <div className="space-y-1.5">
                      <div className="flex items-center justify-between gap-3">
                        <span>简体笔画</span>
                        <span className="shrink-0 whitespace-nowrap font-semibold tabular-nums text-[#C8A655]">
                          {simplifiedStroke > 0 ? simplifiedStroke : '--'}
                        </span>
                      </div>
                      <div className="flex items-center justify-between gap-3">
                        <span>繁体笔画</span>
                        <span className="shrink-0 whitespace-nowrap font-semibold tabular-nums text-[#C8A655]">
                          {traditionalStroke > 0 ? traditionalStroke : '--'}
                        </span>
                      </div>
                      <div className="flex items-center justify-between gap-3">
                        <span>姓名学笔画</span>
                        <span className="shrink-0 whitespace-nowrap font-semibold tabular-nums text-[#C8A655]">
                          {nameologyStroke > 0 ? nameologyStroke : '--'}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </article>

              <article className="rounded-3xl border border-[#E4DDD0] bg-white p-4 sm:p-5">
                <h2 className={`mb-4 text-sm text-[#C8A655] sm:text-[13px] ${KAITI_FONT}`}>《新华字典》释义</h2>
                {xinhuaPinyins.length > 1 && (
                  <p className="mb-3 text-sm text-[#7A6A52] sm:text-[13px]">
                    读音：<span className="font-medium">{xinhuaPinyins.join(' / ')}</span>
                  </p>
                )}
                <div className="rounded-2xl bg-[#FFFDF6] p-4 text-sm leading-relaxed text-[#38322E] whitespace-pre-wrap break-words sm:text-[13px]">
                  {xinhuaRenderBlocks.length > 0 ? (
                    <div className="space-y-1">
                      {xinhuaRenderBlocks.map((block, idx) => {
                        if (block.kind === 'quote') {
                          return (
                            <blockquote
                              key={`q-${idx}`}
                              className="my-1 border-l-2 border-[#DCC9A8] pl-3 text-[#625545] italic"
                            >
                              <div className="space-y-1">
                                {block.lines.map((quoteLine, qIdx) => (
                                  <p key={`q-${idx}-${qIdx}`}>{quoteLine}</p>
                                ))}
                              </div>
                            </blockquote>
                          );
                        }
                        return (
                          <p key={`t-${idx}`} className={block.isBigHeader ? `text-lg sm:text-xl font-semibold ${KAITI_FONT}` : ''}>
                            {block.text}
                          </p>
                        );
                      })}
                    </div>
                  ) : '暂无释义'}
                </div>
              </article>

              {kangxiFormattedText && (
                <article className="rounded-3xl border border-[#E4DDD0] bg-white p-4 sm:p-5">
                  <h2 className={`mb-4 text-sm text-[#C8A655] sm:text-[13px] ${KAITI_FONT}`}>《康熙字典》原文</h2>
                  <div className="rounded-2xl bg-[#FFFDF6] p-4 text-sm leading-relaxed text-[#38322E] whitespace-pre-wrap break-words sm:text-[13px]">
                    {kangxiFormattedText}
                  </div>
                </article>
              )}

              {onOpenSancaiLiuge && (
                <button
                  onClick={() => onOpenSancaiLiuge(charDisplay)}
                  className="mt-1 flex w-full items-center justify-center gap-2 rounded-2xl bg-[#8B6B3D] px-4 py-3 text-base font-medium text-white hover:bg-[#76582F]"
                >
                  进入三才六格测算
                  <ChevronRight className="h-5 w-5" />
                </button>
              )}
            </div>
          )}

          {!searching && !searchError && !hasResult && !notFound && (
            <div className="rounded-2xl border border-dashed border-[#d7cfbf] bg-[linear-gradient(180deg,#f8f5ec_0%,#f2f2f2_100%)] px-4 py-10 text-center text-base text-slate-500">
              输入一个汉字，即可查看本地《康熙字典》与新华释义
            </div>
          )}
        </section>
      </main>
    </div>
  );
};

export default KangxiDictionary;
